import React, { useEffect, useState, useMemo, useRef } from 'react';
import { ChevronRight, CircleAlert, CreditCard, CircleCheckBig, Clock, BadgeInfo } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthProvider';
import { supabase } from '@/lib/supabase';
import { getSignedViewUrl } from '@/lib/getSignedViewUrl';
import type { CarActivityCounts, ActivityCountByUser } from '@/lib/activity/carActivity';

type Props = {
  counts: CarActivityCounts;
  onClick: () => void;
};

const Row: React.FC<{
  icon: React.ReactNode;
  text: string;
  usersByCount: ActivityCountByUser[];
  userAvatars: Map<string, string | null>;
  userDisplayNames: Map<string, string>;
  backgroundColor: string;
}> = ({ icon, text, usersByCount, userAvatars, userDisplayNames, backgroundColor }) => (
  <div className="flex items-center gap-3 text-sm text-gray-700">
    <div 
      className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
      style={{ backgroundColor }}
    >
      {icon}
    </div>
    <div className="flex-1">{text}</div>
    {/* User avatars */}
    {usersByCount.length > 0 && (
      <div className="flex items-center gap-1.5 flex-shrink-0">
        {usersByCount.map((userCount) => {
          const avatarUrl = userAvatars.get(userCount.userId) || null;
          const displayName = userDisplayNames.get(userCount.userId) || 'User';
          const initial = displayName[0]?.toUpperCase() || 'U';
          return (
            <div
              key={userCount.userId}
              className="relative group"
              title={`${userCount.count} by ${displayName}`}
            >
              {avatarUrl ? (
                <div className="w-6 h-6 rounded-full overflow-hidden border border-gray-200">
                  <img
                    src={avatarUrl}
                    alt={displayName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<div class="w-6 h-6 rounded-full bg-[#7c9ffa] flex items-center justify-center text-white text-[10px] font-semibold border border-gray-200">${initial}</div>`;
                      }
                    }}
                  />
                </div>
              ) : (
                <div className="w-6 h-6 rounded-full bg-[#7c9ffa] flex items-center justify-center text-white text-[10px] font-semibold border border-gray-200">
                  {initial}
                </div>
              )}
              {/* Show count badge if multiple items by this user */}
              {userCount.count > 1 && (
                <div 
                  className="absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full text-[8px] font-bold flex items-center justify-center border border-white"
                  style={{ backgroundColor: '#e7fe52', color: '#070707' }}
                >
                  {userCount.count >= 100 ? '...' : userCount.count}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}
  </div>
);

const NewActivityCard: React.FC<Props> = ({ counts, onClick }) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const [userAvatars, setUserAvatars] = useState<Map<string, string | null>>(new Map());
  const [userDisplayNames, setUserDisplayNames] = useState<Map<string, string>>(new Map());
  const fetchedUserIdsRef = useRef<string>(''); // Track which user IDs we've already fetched

  // Collect all unique user IDs from counts and create a stable key
  const { allUserIds, userIdsKey } = useMemo(() => {
    const userIds = new Set<string>();
    counts.issuesByUser.forEach((u) => userIds.add(u.userId));
    counts.tasksByUser.forEach((u) => userIds.add(u.userId));
    counts.servicesByUser.forEach((u) => userIds.add(u.userId));
    counts.expensesByUser.forEach((u) => userIds.add(u.userId));
    counts.carDetailsByUser.forEach((u) => userIds.add(u.userId));
    const key = Array.from(userIds).sort().join(',');
    return { allUserIds: userIds, userIdsKey: key };
  }, [
    // Use JSON.stringify to create stable dependency keys from arrays
    JSON.stringify(counts.issuesByUser.map((u) => u.userId).sort()),
    JSON.stringify(counts.tasksByUser.map((u) => u.userId).sort()),
    JSON.stringify(counts.servicesByUser.map((u) => u.userId).sort()),
    JSON.stringify(counts.expensesByUser.map((u) => u.userId).sort()),
    JSON.stringify(counts.carDetailsByUser.map((u) => u.userId).sort()),
  ]);

  // Fetch avatars and display names for all users
  useEffect(() => {
    if (allUserIds.size === 0 || !user) return;

    // Skip if we've already fetched avatars for these exact user IDs
    if (fetchedUserIdsRef.current === userIdsKey) {
      return;
    }

    // Mark that we're fetching for these user IDs
    fetchedUserIdsRef.current = userIdsKey;

    const fetchUserData = async () => {
      const userIdsArray = Array.from(allUserIds);
      console.log(
        `🖼️ [NewActivityCard] Fetching avatars for ${userIdsArray.length} users:`,
        userIdsArray.map((id) => id.substring(0, 8))
      );

      const avatarMap = new Map<string, string | null>();
      const displayNameMap = new Map<string, string>();

      // Fetch user data from public.users table
      const { data: usersData, error } = await supabase
        .from('users')
        .select('id, custom_avatar_url, email')
        .in('id', userIdsArray);

      if (error) {
        console.error('❌ [NewActivityCard] Error fetching user data:', error);
        setUserAvatars(avatarMap);
        setUserDisplayNames(displayNameMap);
        return;
      }

      // For current user, get display name from auth metadata
      if (user) {
        const meta: any = user?.user_metadata || {};
        const displayName =
          meta.display_name ||
          meta.full_name ||
          meta.name ||
          (meta.given_name && meta.family_name ? `${meta.given_name} ${meta.family_name}` : null) ||
          user.email?.split('@')[0] ||
          'User';
        displayNameMap.set(user.id, displayName);
      }

      // Fetch display names for other users via Edge Function
      const otherUserIds = userIdsArray.filter((id) => id !== user.id);
      if (otherUserIds.length > 0) {
        try {
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || '';
            const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

            const response = await fetch(`${supabaseUrl}/functions/v1/get-user-display-names`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${session.access_token}`,
                apikey: supabaseAnonKey,
              },
              body: JSON.stringify({ userIds: otherUserIds }),
            });

            if (response.ok) {
              const data = await response.json();
              if (data.success && data.displayNames) {
                Object.entries(data.displayNames).forEach(([userId, displayName]) => {
                  displayNameMap.set(userId, displayName as string);
                });
              }
            }
          }
        } catch (error) {
          console.error('❌ [NewActivityCard] Error fetching display names:', error);
        }
      }

      // For each user, try to get avatar
      for (const userId of userIdsArray) {
        const userData = usersData?.find((u) => u.id === userId);
        let avatarUrl: string | null = null;

        // Try custom avatar first
        if (userData?.custom_avatar_url) {
          try {
            const signedUrlInfo = await getSignedViewUrl(userData.custom_avatar_url);
            avatarUrl = signedUrlInfo.url;
            console.log(`✅ [NewActivityCard] Loaded custom avatar for user ${userId.substring(0, 8)}`);
          } catch (error) {
            console.warn(`⚠️ [NewActivityCard] Failed to load custom avatar:`, error);
          }
        }

        // If no custom avatar and it's the current user, try Google avatar from metadata
        if (!avatarUrl && userId === user.id) {
          const meta: any = user?.user_metadata || {};
          avatarUrl = meta.avatar_url || meta.picture || null;
          if (avatarUrl) {
            console.log(`✅ [NewActivityCard] Using Google avatar for current user`);
          }
        }

        avatarMap.set(userId, avatarUrl);
      }

      console.log(
        `✅ [NewActivityCard] Loaded ${Array.from(avatarMap.values()).filter((v) => v !== null).length} avatars out of ${userIdsArray.length} users`
      );
      setUserAvatars(avatarMap);
      setUserDisplayNames(displayNameMap);
    };

    fetchUserData();
  }, [userIdsKey, user, allUserIds.size]);

  const rows: Array<{
    key: string;
    count: number;
    icon: React.ReactNode;
    text: string;
    usersByCount: ActivityCountByUser[];
    backgroundColor: string;
  }> = [
    {
      key: 'issues',
      count: counts.issuesNew,
      icon: <CircleAlert className="w-4 h-4" style={{ color: '#8fadfb' }} />,
      text:
        t('activity.summary.issues', { count: counts.issuesNew }) ||
        `${counts.issuesNew} new issue reported`,
      usersByCount: counts.issuesByUser,
      backgroundColor: '#eff2fc',
    },
    {
      key: 'expenses',
      count: counts.expensesAdded,
      icon: <CreditCard className="w-4 h-4" style={{ color: '#F4A261' }} />,
      text:
        t('activity.summary.expenses', { count: counts.expensesAdded }) ||
        `${counts.expensesAdded} expenses added`,
      usersByCount: counts.expensesByUser,
      backgroundColor: '#FFF7E6',
    },
    {
      key: 'services',
      count: counts.serviceRecordsUpdated,
      icon: <CircleCheckBig className="w-4 h-4" style={{ color: '#4db883' }} />,
      text:
        t('activity.summary.services', { count: counts.serviceRecordsUpdated }) ||
        `Service record updated`,
      usersByCount: counts.servicesByUser,
      backgroundColor: '#ebfce7',
    },
    {
      key: 'tasks',
      count: counts.tasksUpdated,
      icon: <Clock className="w-4 h-4" style={{ color: '#34e5ec' }} />,
      text:
        t('activity.summary.tasks', { count: counts.tasksUpdated }) ||
        `${counts.tasksUpdated} tasks updated`,
      usersByCount: counts.tasksByUser,
      backgroundColor: '#e3fafb',
    },
    {
      key: 'carDetails',
      count: counts.carDetailsUpdated,
      icon: <BadgeInfo className="w-4 h-4" style={{ color: '#c95060' }} />,
      text: t('activity.summary.carDetails') || 'Car details updated',
      usersByCount: counts.carDetailsByUser,
      backgroundColor: '#f1d2d6',
    },
  ].filter((r) => r.count > 0);

  if (rows.length === 0) return null;

  return (
    <Card
      className="w-full rounded-[20px] bg-[#FDFDFD] cursor-pointer"
      style={{ boxShadow: '0px 10px 8px -1px #EEEEEE' }}
      onClick={onClick}
      role="button"
      aria-label={t('activity.card.aria') || 'New activity'}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-base font-semibold text-black" style={{ fontFamily: 'Lato', fontWeight: 700 }}>
              {t('activity.card.title') || 'New activity'}
            </h3>
            <p 
              className="text-gray-600 mt-1"
              style={{
                fontFamily: 'Lato',
                fontWeight: 400,
                fontStyle: 'normal',
                fontSize: '12px',
                lineHeight: '16px',
                letterSpacing: '0.1px'
              }}
            >
              {t('activity.card.byOthers') || 'Tap to see what others have done and to dismiss notifications'}
            </p>
          </div>
          <ChevronRight className="w-5 h-5 text-gray-400 mt-1" />
        </div>

        <div className="mt-4 space-y-3">
          {rows.map((r) => (
            <Row
              key={r.key}
              icon={r.icon}
              text={r.text}
              usersByCount={r.usersByCount}
              userAvatars={userAvatars}
              userDisplayNames={userDisplayNames}
              backgroundColor={r.backgroundColor}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

export default NewActivityCard;
